import React from "react";
import { Box, TextField } from "@mui/material";
import { QuestionCard } from "../shared/QuestionCard";
import { StyledChoicePill } from "../form/StyledChoicePill";
import { HealthIssue, HealthIssueMeta } from "@/helpers/enums";

const NONE = "NONE_OPTION";

type Props = {
  dogName: string;
  value: string[];
  otherText?: string;
  onChange: (issues: string[], otherText?: string) => void;
};

export function HealthIssuesQuestion({
  dogName,
  value,
  otherText = "",
  onChange,
}: Props) {
  const hasNoneSelected = value.includes(NONE);

  const toggleIssue = (item: HealthIssue | typeof NONE) => {
    if (item === NONE) {
      // Toggle "None" - clears all issues and selects None
      if (hasNoneSelected) {
        onChange([], ""); // Deselect None
      } else {
        onChange([NONE], ""); // Select None, clear issues
      }
      return;
    }

    // Selecting an issue - remove NONE if present
    const withoutNone = value.filter((i) => i !== NONE);
    const updated = withoutNone.includes(item)
      ? withoutNone.filter((i) => i !== item)
      : [...withoutNone, item];

    // Clear other text if "Other" is being unselected
    if (item === HealthIssue.OTHER && withoutNone.includes(item)) {
      onChange(updated, "");
    } else {
      onChange(updated, otherText);
    }
  };

  const updateOtherText = (text: string) => {
    onChange(value, text);
  };

  const showOtherInput = value.includes(HealthIssue.OTHER);

  return (
    <QuestionCard
      title={`Does ${dogName || "your dog"} have any health issues?`}
      subtitle="Select all that apply"
    >
      {/* "None" option - separated */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          mb: "10px",
          pb: "10px",
          borderBottom: "1px solid #E8E0D5",
        }}
      >
        <StyledChoicePill
          selected={hasNoneSelected}
          label="No health issues"
          big
          onClick={() => toggleIssue(NONE)}
        />
      </Box>

      {/* Actual health issues */}
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: { xs: 1, sm: 1.25 },
          justifyContent: "center",
          alignItems: "stretch",
        }}
      >
        {Object.values(HealthIssue).map((h) => (
          <StyledChoicePill
            key={h}
            selected={value.includes(h)}
            label={HealthIssueMeta[h].label}
            onClick={() => toggleIssue(h)}
          />
        ))}
      </Box>

      {showOtherInput && (
        <Box sx={{ mt: 2, width: "100%" }}>
          <TextField
            multiline
            minRows={2}
            fullWidth
            placeholder="Please describe the issue"
            value={otherText}
            onChange={(e) => updateOtherText(e.target.value)}
            sx={{
              width: "100%",
              "& .MuiOutlinedInput-root": {
                backgroundColor: "#fff",
                borderRadius: "12px",
                "& fieldset": { borderColor: "rgba(74,48,38,0.25)" },
                "&:hover fieldset": { borderColor: "rgba(74,48,38,0.45)" },
                "&.Mui-focused fieldset": { borderColor: "#4A3026" },

                "& textarea": {
                  padding: "14px 16px",
                  fontSize: { xs: "0.95rem", sm: "1rem" },
                  fontWeight: 600,
                  color: "#4A3026",
                  backgroundColor: "#fff",
                  lineHeight: 1.35,
                },
              },
            }}
          />
        </Box>
      )}
    </QuestionCard>
  );
}
